import { VideoDecodeProcessorParam, VideoEncodeProcessorParam } from '../../manager/video-manager';
import { getStartCodeLengthAt, NALU, parseNALUs } from '../nalu';


/**
* 将业务侧发的 sei message 封装成 sei NALU
* @param {number} type
* @param {ArrayBuffer} buffer
* @returns {NALU}
*/
export function encodeSEINalu([type, buffer]: SEI_MESSAGE) {
  const payloadSize = buffer.byteLength;
  // payload size 读取规则是读到非 0xff 为止。
  // payload size = a * 0xff + b;
  const a = parseInt(String(payloadSize / 255), 10);
  const b = payloadSize % 255;
  // 0x00000001, 0x6, 0x5, a 个 0xff, b, sei 内容, 0x80 sei 固定结尾。
  const arr = [];
  arr.push(0, 0, 0, 1, 6, type);
  for (let i = 0; i < a; i++) {
    arr.push(255);
  }
  arr.push(b);
  const seiContentView = new DataView(buffer);
  arr.push(...new Uint8Array(seiContentView.buffer));
  // sei nalu 结尾 0x80
  arr.push(128);
  return new NALU(new DataView(new Uint8Array(arr).buffer), true);
}

export function isEmptyFrame(frame: RTCEncodedVideoFrame) {
  return frame.type === 'empty' || frame.data.byteLength === 0;
}

/**
 * 判断 ArrayBuffer 中是否包含 SEI NALU
 * 只检查 NALU 类型，找到 SEI 立即返回，避免解析所有 NALU
 * @param arrayBuffer - 包含 NALU 数据的 ArrayBuffer
 * @returns 是否包含 SEI NALU
 */
export function hasSEI(arrayBuffer: ArrayBuffer): boolean {
  const dataView = new DataView(arrayBuffer);
  let offset = 0;

  while (offset < dataView.byteLength) {
    const startCodeLength = getStartCodeLengthAt(dataView, offset);

    if (startCodeLength === 0) {
      offset += 1;
      continue;
    }

    // 检查 NALU 类型（起始码后第一个字节的低 5 位）
    const naluHeaderIndex = offset + startCodeLength;
    if (naluHeaderIndex < dataView.byteLength) {
      const naluType = dataView.getUint8(naluHeaderIndex) & 0x1f;
      if (naluType === 6) { // sei nalu type 是 6
        return true;
      }
    }

    // 跳过起始码，继续查找下一个 NALU
    offset += startCodeLength + 1;
  }

  return false;
}

/**
* 获取视频帧中有几个 nalu
* @returns {Number}
*/
export function getNaluCount(arrayBuffer: ArrayBuffer) {
  let zeroCount = 0;
  let naluCount = 0;
  const dataView = new DataView(arrayBuffer);
  for (let i = 0; i < arrayBuffer.byteLength; i++) {
    switch (dataView.getUint8(i)) {
      case 0:
        zeroCount++;
        break;
      case 1:
        // nalu start code 001, 0001
        if (zeroCount === 2 || zeroCount === 3) {
          naluCount++;
        }
        zeroCount = 0;
        break;
      default:
        zeroCount = 0;
        break;
    }
  }

  return naluCount;
}

export function encodeSEI({ frame, seiMessageList }: VideoEncodeProcessorParam) {
  if (!seiMessageList || seiMessageList.length === 0 || frame.data.byteLength === 0) return frame;
  const naluCount = getNaluCount(frame.data);
  // sei nalu + 视频帧 nalu 不能超过 9，否则会丢帧。
  const seiCountToSend = 9 - naluCount;
  if (seiCountToSend <= 0) return frame;
  // 与 native 对齐，将 sei nalu 逆序插入到视频帧 nalu 之前
  // sendSEI 1 2 3 => seiNalu(3), seiNalu(2), seiNalu(1), videoNalu
  const seiNaluList = seiMessageList
    .splice(0, seiCountToSend)
    .reverse()
    .map(encodeSEINalu);
  const totalSEINaluByteLength = seiNaluList.reduce(
    (result, current) => result + current.dataView.byteLength,
    0
  );
  const arrayBuffer = new ArrayBuffer(totalSEINaluByteLength + frame.data.byteLength);
  const dataView = new DataView(arrayBuffer);
  const videoFrameDataView = new DataView(frame.data);
  let index = 0;
  // 将 sei nalu 塞到 video frame nalu 之前
  for (let i = 0; i < seiNaluList.length; i++) {
    for (let j = 0; j < seiNaluList[i].dataView.byteLength; j++) {
      dataView.setInt8(index++, seiNaluList[i].dataView.getInt8(j));
    }
  }
  for (let i = 0; i < frame.data.byteLength; i++) {
    dataView.setInt8(index++, videoFrameDataView.getInt8(i));
  }
  frame.data = arrayBuffer;
  return frame;
}

export function decodeSEI({ frame, onSEI }: VideoDecodeProcessorParam): RTCEncodedVideoFrame {
  try {
    if (isEmptyFrame(frame) || !hasSEI(frame.data)) return frame;
    const seiNaluList = parseNALUs(frame.data, false).filter(nalu => nalu.isSEI);
    onSEI?.(seiNaluList.reverse());
  } catch (error) {
  }
  return frame;
}

export type SEI_MESSAGE = [number, ArrayBuffer];
